import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useImpersonation, useAuthStore } from '@/stores/auth.store';
import { useCompanySettings } from '@/hooks/settings/useCompanySettings';
import { useStopImpersonation } from '@/hooks/useImpersonation';

/**
 * Sticky top banner shown on every authenticated page when an impersonation
 * session is active. Renders nothing in the normal case so it has zero cost
 * outside of impersonation flows.
 *
 * Copy mirrors the spec: "You're impersonating <user> at <tenant>. Actions
 * you take are logged. [Stop impersonating]". The button uses the shared
 * `useStopImpersonation` hook so the banner and the IconRail logout share a
 * single wind-down path.
 */
export function ImpersonationBanner() {
  const impersonation = useImpersonation();
  const userEmail = useAuthStore((s) => s.email);
  const userId = useAuthStore((s) => s.userId);
  const companyQuery = useCompanySettings();
  const stop = useStopImpersonation();
  const [stopping, setStopping] = useState(false);

  if (!impersonation.active) return null;

  const userLabel = userEmail ?? userId ?? 'this user';
  const tenantLabel = companyQuery.data?.name?.trim() || 'this tenant';

  async function handleStop() {
    if (stopping) return;
    setStopping(true);
    try {
      await stop();
    } finally {
      // If we're still mounted, drop the spinner. In practice the page either
      // closes (window.close) or navigates (location.href = /login), so this
      // is mostly belt-and-suspenders.
      setStopping(false);
    }
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      // Red-amber gradient + tight padding; sits above the existing dashboard
      // shell. position: sticky lets it pin to the viewport top inside the
      // scroll container when one wraps the whole app.
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 border-b border-red-900/40 bg-gradient-to-r from-red-700 via-red-600 to-amber-600 px-4 py-2 text-sm font-medium text-white shadow-md"
    >
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">
          You&apos;re impersonating{' '}
          <span className="font-semibold underline decoration-white/40 decoration-2 underline-offset-2">
            {userLabel}
          </span>{' '}
          at{' '}
          <span className="font-semibold underline decoration-white/40 decoration-2 underline-offset-2">
            {tenantLabel}
          </span>
          . Actions you take are logged.
        </span>
      </div>
      <button
        type="button"
        onClick={handleStop}
        disabled={stopping}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white ring-1 ring-inset ring-white/30 transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white disabled:cursor-wait disabled:opacity-70"
      >
        {stopping ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Stopping…
          </>
        ) : (
          'Stop impersonating'
        )}
      </button>
    </div>
  );
}
